<template>
    <div>
        <label class="mb-1">{{ label }}</label>
        <multiselect
            :value="value"
            @input="onChange"
            @open="fetchOptions"
            @search-change="onSearchChange"
            :internal-search="!hasPagination"
            :options="options"
            :track-by="trackBy"
            :customLabel="getLabel"
            :loading="isLoading"
            :disabled="disabled"
            placeholder="Select a value">
        </multiselect>
        <small v-if="values.length > 1">
            Multiple options selected
        </small>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {withConfirmation} from '@/decorators/withConfirmation'
import {DOC_FIELD_UPDATE, DOC_LINES_BATCH_UPDATE} from '@/store/modules/doc-viewer'
import {checkFieldSelectOptionsHasPagination} from '@/types/document-viewer/field/modules/editor/select'
import {getFormFieldFormattedLabel} from '@/types/document-viewer/field/modules/editor'
import {getFieldPathMeta} from '@/types/document-viewer/field/modules/path'
import {tryAutofillPricingFormOnLineFieldChange} from '@/views/documents/single/view/lines/calculations'
import {evaluateTeamLangPlaceholder} from '@/views/settings/team/lang'

export default {
    name: 'LineFieldBatchUpdateSelect',
    props: {
        field: { // FormField
            type: Object,
            required: true
        },
        values: {
            type: Array,
            required: true
        },
        getOptions: {
            type: Function,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            options: [],
            isLoading: false
        }
    },
    computed: {
        ...mapGetters('doc_viewer', [
            'lines'
        ]),

        value() {
            return this.values.length === 1 ? this.values[0] : null
        },

        label() {
            return evaluateTeamLangPlaceholder(this.field.label)
        },

        editor() {
            return this.field.editor || {}
        },

        select() {
            return this.editor.select || {}
        },

        trackBy() {
            return this.select.trackBy
        },

        hasPagination() {
            return checkFieldSelectOptionsHasPagination(this.select)
        }
    },
    methods: {
        ...mapActions('doc_viewer', {
            updateField: DOC_FIELD_UPDATE,
            updateLinesData: DOC_LINES_BATCH_UPDATE
        }),

        getLabel(value) {
            return getFormFieldFormattedLabel({editor: this.editor, value})
        },

        onChange(v) {
            if (v && v[this.trackBy]) {
                withConfirmation({
                    title: `Update "${this.label}"`,
                    description: `Do you want to update all the items to match: </br> "${this.getLabel(v)}"?`,
                    onConfirm: () => this.updateItems(v)
                })
            } else {
                withConfirmation({
                    title: `Clear "${this.label}"`,
                    description: `Do you want to clear all items for this field?`,
                    onConfirm: () => this.updateItems(null)
                })
            }
        },

        async updateItems(value) {
            const data = []
            const {prop, path} = getFieldPathMeta(this.field.path)
            const pathWithoutLinesPrefix = path.split('.').slice(1).join('.')
            this.lines.forEach((line, idx) => {
                data.push({idx, path, data: value})
            })
            if (data.length !== 0) {
                await this.updateLinesData(data.map(f => ({...f, path: pathWithoutLinesPrefix})))
            }

            if (prop === 'taxType') {
                for (const line of this.lines) {
                    const lineIdx = this.lines.indexOf(line)
                    await tryAutofillPricingFormOnLineFieldChange({lineIdx, fieldPath: 'taxType'})
                }
            }
        },

        async fetchOptions() {
            this.isLoading = true
            this.options = await this.getOptions()
            this.isLoading = false
        },

        async onSearchChange(query) {
            if (!this.hasPagination) { // internal search will work
                return
            }
            this.isLoading = true
            this.options = await this.getOptions(query)
            this.isLoading = false
        }
    }
}
</script>
